import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Layers, FileText } from "lucide-react";
import clsx from "clsx";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { Accent, Card, Lane, Thread } from "../lib/types";
import { accentClasses } from "../lib/types";
import { api } from "../lib/api";
import { LaneGrid } from "./LaneGrid";
import { CardItem } from "./CardItem";
import { FlatCanvas } from "./FlatCanvas";
import { CardModal } from "./CardModal";
import { InboxColumn } from "./InboxColumn";
import { useToast } from "../lib/toast";

type Props = {
  thread: Thread;
  onThreadUpdated: (t: Thread) => void;
  onCardDeleted?: () => void;
};

export function ThreadView({ thread, onThreadUpdated, onCardDeleted }: Props) {
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [activeDrag, setActiveDrag] = useState<{
    type: "card" | "lane";
    id: string;
  } | null>(null);
  const [renamingTitle, setRenamingTitle] = useState(false);
  const [convertingKind, setConvertingKind] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const { toast } = useToast();

  // Fetch thread contents whenever the active thread changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getThread(thread.id).then((res) => {
      if (cancelled) return;
      setLanes(res.lanes);
      setCards(res.cards);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [thread.id]);

  const cardsByLane = useMemo(() => {
    const map = new Map<string | null, Card[]>();
    for (const c of cards) {
      const k = c.lane_id ?? null;
      const arr = map.get(k) ?? [];
      arr.push(c);
      map.set(k, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.sort_order - b.sort_order);
    }
    return map;
  }, [cards]);

  const looseCards = cardsByLane.get(null) ?? [];

  const persistCardOrder = useCallback(
    (next: Card[]) => {
      // After local reorder, sync sort_order + lane_id for affected cards.
      const items = next.map((c, i) => ({
        id: c.id,
        sort_order: i,
        lane_id: c.lane_id,
      }));
      api.reorder("card", items).catch(console.error);
    },
    []
  );

  const handleDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as { type?: string } | undefined;
    if (data?.type === "card" || data?.type === "lane") {
      setActiveDrag({ type: data.type, id: String(e.active.id) });
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const aData = active.data.current as any;
    const oData = over.data.current as any;

    if (aData?.type === "lane" && oData?.type === "lane") {
      // Lane reorder.
      const oldIdx = lanes.findIndex((l) => l.id === active.id);
      const newIdx = lanes.findIndex((l) => l.id === over.id);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      const next = arrayMove(lanes, oldIdx, newIdx).map((l, i) => ({
        ...l,
        sort_order: i,
      }));
      setLanes(next);
      api
        .reorder(
          "lane",
          next.map((l) => ({ id: l.id, sort_order: l.sort_order }))
        )
        .catch(console.error);
      return;
    }

    if (aData?.type === "card") {
      // Card drop. Determine the target lane and the index inside it.
      const activeCard = cards.find((c) => c.id === active.id);
      if (!activeCard) return;

      let targetLaneId: string | null | undefined = undefined;
      let targetIndex = -1;

      // A lane has two registered drop-zones with `data.type === "lane"`:
      //   1. the outer sortable (so the lane itself can be reordered) — its
      //      id is `lane.id` and data is `{ type, lane }`.
      //   2. the inner content area droppable — its id is
      //      `lane-drop-<lane.id>` and data is `{ type, laneId }`.
      // The old code only handled (2), so a card dropped on a lane header hit
      // (1) and got targetLaneId = undefined, which silently wiped lane_id
      // and the card disappeared in lanes mode. Resolve from any signal we
      // have, then validate the lane still exists.
      const resolveLaneId = (): string | null | undefined => {
        if (oData?.type !== "lane") return undefined;
        const candidate =
          (oData.laneId as string | undefined) ??
          (oData.lane?.id as string | undefined) ??
          (typeof over.id === "string" ? over.id : undefined);
        if (!candidate) return undefined;
        return lanes.some((l) => l.id === candidate) ? candidate : undefined;
      };

      if (oData?.type === "card") {
        const overCard = cards.find((c) => c.id === over.id);
        if (!overCard) return;
        targetLaneId = overCard.lane_id;
        const list = (cardsByLane.get(targetLaneId) ?? []).filter(
          (c) => c.id !== active.id
        );
        const overIdx = list.findIndex((c) => c.id === over.id);
        targetIndex = overIdx === -1 ? list.length : overIdx;
      } else if (oData?.type === "lane") {
        const resolved = resolveLaneId();
        if (resolved === undefined) {
          // Couldn't resolve a real lane — bail out so the card snaps back
          // to its original position rather than vanishing.
          return;
        }
        targetLaneId = resolved;
        const list = (cardsByLane.get(targetLaneId) ?? []).filter(
          (c) => c.id !== active.id
        );
        targetIndex = list.length;
      } else if (oData?.type === "flat") {
        targetLaneId = null;
        const list = (cardsByLane.get(null) ?? []).filter(
          (c) => c.id !== active.id
        );
        targetIndex = list.length;
      } else if (oData?.type === "inbox") {
        // Inbox column in lanes view holds loose cards. Drop here un-assigns
        // the card from whatever lane it was in.
        targetLaneId = null;
        const list = (cardsByLane.get(null) ?? []).filter(
          (c) => c.id !== active.id
        );
        targetIndex = list.length;
      } else {
        // Dropped on empty space / unknown target → snap back.
        return;
      }

      // Final safety net: never accept undefined or a non-existent lane.
      if (targetLaneId === undefined) return;
      if (
        targetLaneId !== null &&
        !lanes.some((l) => l.id === targetLaneId)
      ) {
        return;
      }
      if (targetIndex < 0) return;

      const next = cards.map((c) => ({ ...c }));
      const moving = next.find((c) => c.id === active.id)!;
      moving.lane_id = targetLaneId;

      // Rebuild order within target lane.
      const targetList = next
        .filter((c) => (c.lane_id ?? null) === (targetLaneId ?? null) && c.id !== active.id)
        .sort((a, b) => a.sort_order - b.sort_order);
      targetList.splice(targetIndex, 0, moving);
      targetList.forEach((c, i) => {
        c.sort_order = i;
      });

      // Compact sort orders for the source lane too (so old position closes up).
      if ((activeCard.lane_id ?? null) !== (targetLaneId ?? null)) {
        const sourceList = next
          .filter(
            (c) =>
              (c.lane_id ?? null) === (activeCard.lane_id ?? null) &&
              c.id !== active.id
          )
          .sort((a, b) => a.sort_order - b.sort_order);
        sourceList.forEach((c, i) => {
          c.sort_order = i;
        });
      }

      setCards(next);

      // Persist: send updated sort+lane for everything in target (and source) lane.
      const items: { id: string; sort_order: number; lane_id: string | null }[] = [];
      const seen = new Set<string>();
      const collect = (laneId: string | null) => {
        next
          .filter((c) => (c.lane_id ?? null) === laneId)
          .sort((a, b) => a.sort_order - b.sort_order)
          .forEach((c) => {
            if (!seen.has(c.id)) {
              items.push({ id: c.id, sort_order: c.sort_order, lane_id: c.lane_id });
              seen.add(c.id);
            }
          });
      };
      collect(targetLaneId);
      if ((activeCard.lane_id ?? null) !== (targetLaneId ?? null)) {
        collect(activeCard.lane_id ?? null);
      }
      api.reorder("card", items).catch(console.error);
    }
  };

  const addLane = async () => {
    const lane = await api.createLane(thread.id, "New");
    setLanes((prev) => [...prev, lane]);
  };

  const renameLane = async (laneId: string, name: string) => {
    setLanes((prev) => prev.map((l) => (l.id === laneId ? { ...l, name } : l)));
    await api.updateLane(laneId, { name }).catch(console.error);
  };
  const setLaneAccent = async (laneId: string, accent: Lane["accent"]) => {
    setLanes((prev) => prev.map((l) => (l.id === laneId ? { ...l, accent } : l)));
    await api.updateLane(laneId, { accent }).catch(console.error);
  };
  const deleteLane = async (laneId: string) => {
    setLanes((prev) => prev.filter((l) => l.id !== laneId));
    setCards((prev) =>
      prev.map((c) => (c.lane_id === laneId ? { ...c, lane_id: null } : c))
    );
    await api.deleteLane(laneId).catch(console.error);
  };

  const addCard = async (laneId: string | null, title: string) => {
    const card = await api.createCard(thread.id, laneId, title);
    setCards((prev) => [...prev, card]);
  };

  const updateCard = async (cardId: string, patch: Partial<Card>) => {
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, ...patch } : c)));
    await api.updateCard(cardId, patch).catch(console.error);
  };

  const deleteCard = async (cardId: string) => {
    // Soft-delete: remove from local view + send to bin. We snapshot the
    // pre-delete card so the toast's Undo can restore the exact same
    // record. Restore re-applies its original lane_id / sort_order on the
    // server side, and we re-insert it into local state on success.
    const snapshot = cards.find((c) => c.id === cardId);
    setCards((prev) => prev.filter((c) => c.id !== cardId));
    await api.deleteCard(cardId).catch(console.error);
    onCardDeleted?.();
    if (!snapshot) return;
    toast({
      message: "card moved to bin",
      duration: 6000,
      action: {
        label: "undo",
        onClick: async () => {
          try {
            const restored = await api.restoreCard(cardId);
            setCards((prev) =>
              prev.some((c) => c.id === restored.id)
                ? prev
                : [...prev, restored]
            );
            onCardDeleted?.();
          } catch (e) {
            // If the server restore fails (e.g. the card was purged in the
            // 6-second window), do nothing — the bin view still has it.
            console.error(e);
          }
        },
      },
    });
  };

  /**
   * Promote the inbox (loose cards) into a real lane. Creates a new lane at
   * the end of the lanes list and assigns every currently-loose card to it,
   * preserving their current sort order. The inbox empties and auto-hides.
   */
  const promoteInbox = async (name: string) => {
    const lane = await api.createLane(thread.id, name);
    // Capture the loose cards from the live state right now — these are the
    // cards in their current Inbox order. We assign each a sort_order to
    // match its index in this array so the new lane reflects the inbox's
    // top-to-bottom order exactly.
    const looseInOrder = looseCards.slice();
    setLanes((prev) => [...prev, lane]);
    setCards((prev) =>
      prev.map((c) => {
        const idx = looseInOrder.findIndex((l) => l.id === c.id);
        if (idx === -1) return c;
        return { ...c, lane_id: lane.id, sort_order: idx };
      })
    );
    // Persist the moves in one batch.
    await api
      .reorder(
        "card",
        looseInOrder.map((c, i) => ({
          id: c.id,
          sort_order: i,
          lane_id: lane.id,
        }))
      )
      .catch(console.error);
  };

  const accent = accentClasses[thread.accent];

  // Hot key: "n" creates a card in the first lane (or flat).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName.toLowerCase();
        if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      }
      if (e.key === "n" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        if (thread.kind === "lanes") {
          const first = lanes[0];
          if (first) addCard(first.id, "Untitled");
        } else {
          addCard(null, "Untitled");
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, thread.kind, thread.id]);

  const activeCard =
    activeDrag?.type === "card" ? cards.find((c) => c.id === activeDrag.id) ?? null : null;

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden">
      <header className="flex shrink-0 items-center justify-between px-8 pt-6 pb-4">
        <div className="flex items-center gap-3">
          <span className={clsx("h-2.5 w-2.5 rounded-full", accent.dot)} />
          {renamingTitle ? (
            <input
              autoFocus
              defaultValue={thread.name}
              onBlur={async (e) => {
                const v = e.target.value.trim();
                setRenamingTitle(false);
                if (v && v !== thread.name) {
                  const next = await api.updateThread(thread.id, { name: v });
                  onThreadUpdated(next);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") setRenamingTitle(false);
              }}
              className="input-bare text-2xl font-semibold tracking-tight"
            />
          ) : (
            <h2
              onDoubleClick={() => setRenamingTitle(true)}
              className="cursor-text text-2xl font-semibold tracking-tight"
              title="double-click to rename"
            >
              {thread.name}
            </h2>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Kind toggle */}
          <div className="flex items-center gap-0.5 rounded-lg border border-border bg-surface p-0.5">
            <button
              disabled={convertingKind}
              onClick={async () => {
                if (thread.kind === "lanes") return;
                setConvertingKind(true);
                const next = await api.updateThread(thread.id, { kind: "lanes" });
                onThreadUpdated(next);
                if (lanes.length === 0) {
                  const lane = await api.createLane(thread.id, "New");
                  setLanes([lane]);
                }
                setConvertingKind(false);
              }}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors",
                thread.kind === "lanes"
                  ? "bg-canvas text-ink"
                  : "text-ink-mute hover:text-ink-soft"
              )}
            >
              <Layers size={12} /> lanes
            </button>
            <button
              disabled={convertingKind}
              onClick={async () => {
                if (thread.kind === "flat") return;
                setConvertingKind(true);
                const next = await api.updateThread(thread.id, { kind: "flat" });
                onThreadUpdated(next);
                setConvertingKind(false);
              }}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors",
                thread.kind === "flat"
                  ? "bg-canvas text-ink"
                  : "text-ink-mute hover:text-ink-soft"
              )}
            >
              <FileText size={12} /> flat
            </button>
          </div>

          {thread.kind === "lanes" && (
            <button
              onClick={addLane}
              className="btn-soft text-xs"
            >
              <Plus size={12} /> lane
            </button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-ink-mute">
          loading...
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {thread.kind === "lanes" ? (
            // overflow-x-hidden by design: lanes reflow into a
            // masonry grid rather than scrolling sideways. Vertical
            // scroll is fine (and expected once you expand cards).
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <div className="flex items-start gap-3 px-8 pt-2 pb-12">
                {/*
                 * Inbox column for loose cards (lane_id = null). Only
                 * rendered when there's at least one loose card so an
                 * empty inbox doesn't occupy space — and a one-click
                 * "save as lane" promotes the inbox's contents into a
                 * real named lane when it grows useful. Fixed-width
                 * sidebar so the responsive lane grid gets the rest.
                 */}
                {looseCards.length > 0 && (
                  <InboxColumn
                    threadAccent={thread.accent}
                    cards={looseCards}
                    onAddCard={(title) => addCard(null, title)}
                    onCardClick={(c) => setEditingCard(c)}
                    onPromote={promoteInbox}
                  />
                )}

                <LaneGrid
                  lanes={lanes}
                  cardsByLane={cardsByLane}
                  onAddCard={(laneId, title) => addCard(laneId, title)}
                  onCardClick={(c) => setEditingCard(c)}
                  onAddLane={addLane}
                  onRenameLane={renameLane}
                  onChangeAccent={setLaneAccent}
                  onDeleteLane={deleteLane}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <FlatCanvas
                cards={looseCards.concat(
                  cards
                    .filter((c) => c.lane_id !== null)
                    .sort((a, b) => a.sort_order - b.sort_order)
                )}
                threadAccent={thread.accent as Accent}
                onAddCard={(title) => addCard(null, title)}
                onCardClick={(c) => setEditingCard(c)}
              />
            </div>
          )}

          <DragOverlay>
            {activeCard ? (
              <div className="rotate-1 opacity-90">
                <CardItem
                  card={activeCard}
                  fallbackAccent={thread.accent}
                  onClick={() => {}}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {editingCard && (
        <CardModal
          card={editingCard}
          fallbackAccent={thread.accent}
          onClose={() => setEditingCard(null)}
          onSave={(patch) => updateCard(editingCard.id, patch)}
          onDelete={() => deleteCard(editingCard.id)}
        />
      )}
    </div>
  );
}
